import type { PeekViewService } from '@affine/core/modules/peek-view';
import { I18n } from '@affine/i18n';
import { streamLainDisclosureStyles } from '@blocksuite/affine-shared/utils';
import { WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import type { Signal } from '@preact/signals-core';
import { css, html, nothing, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';

import {
  type AiDocSourcePillItem,
  docSearchSourceStyles,
  renderDocSearchSourceList,
} from './doc-search-source';

interface DocKeywordSearchToolCall {
  type: 'tool-call';
  toolCallId: string;
  toolName: string;
  args: { query: string };
}

interface DocKeywordSearchToolResult {
  type: 'tool-result';
  toolCallId: string;
  toolName: string;
  args: { query: string };
  result:
    | Array<DocKeywordSearchResultItem>
    | { success?: boolean; data: DocKeywordSearchResultItem[] };
}

type DocKeywordSearchResultItem = AiDocSourcePillItem;

export class DocKeywordSearchResult extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    ${unsafeCSS(streamLainDisclosureStyles)}
    ${unsafeCSS(docSearchSourceStyles)}
  `;

  @property({ attribute: false })
  accessor data!: DocKeywordSearchToolCall | DocKeywordSearchToolResult;

  @property({ attribute: false })
  accessor width: Signal<number | undefined> | undefined;

  @property({ attribute: false })
  accessor onOpenDoc!: (docId: string, sessionId?: string) => void;

  @property({ attribute: false })
  accessor peekViewService!: PeekViewService;

  renderToolCall() {
    const query = this.data.args?.query ?? '';
    return html`<div class="doc-search-activity">
      ${I18n.t('com.streamlain.ai.doc-search.keyword.searching', {
        query,
      })}
    </div>`;
  }

  renderToolResult() {
    if (this.data.type !== 'tool-result') {
      return nothing;
    }
    // Handle both direct array result and { success, data: [...] } wrapper
    const resultArray: DocKeywordSearchResultItem[] = Array.isArray(
      this.data.result
    )
      ? this.data.result
      : Array.isArray(this.data.result?.data)
        ? this.data.result.data
        : [];
    const query =
      this.data.args?.query ||
      resultArray.find(item => item.query?.trim())?.query ||
      '';

    const sourceResults = resultArray.map(item => ({
      ...item,
      title: item.title,
    }));
    return renderDocSearchSourceList(this, sourceResults, query);
  }

  protected override render() {
    if (this.data.type === 'tool-call') {
      return this.renderToolCall();
    }
    return this.renderToolResult();
  }
}
